/**
 * Thẻ «Data & chốt» (thiết kế 2026-10-31-chi-so-sale-tu-marketing-design.md, CEO 1a · 2a · 3a · 9a
 * 31/10/2026). Data từ Pancake qua Marketing và Center; chốt và doanh số là đơn thật. Máy chủ ghép số
 * và cắt phạm vi, màn chỉ vẽ. Ô thiếu số hiện "—", không hiện 0.
 */
import { useMemo, useState } from "react";
import { QueryErrorNote } from "@/components/QueryErrorNote";
import { StatCard } from "@/components/StatCard";
import { useIsMobile } from "@/hooks/useIsMobile";
import { formatVND, formatVNDCompact } from "@/lib/format";
import { type SalesChatMetrics, useSalesChatMetrics } from "../api";
import {
  CHAT_BRANDS,
  CHAT_PERIODS,
  type ChatPeriodKind,
  type ChatRange,
  dayText,
  maybeInt,
  pctText,
  rangeOf,
  sourceLabel,
  todayVN,
  updatedAtText,
} from "../dataChot";
import { BranchTable, CohortTable, FunnelCard, PageTable, SaleTable, SquadTable } from "./DataChotTables";
import { PancakeAccountsPanel } from "./PancakeAccountsPanel";

export function DataChotTab() {
  const today = useMemo(() => todayVN(), []);
  const [kind, setKind] = useState<ChatPeriodKind>("today");
  const [custom, setCustom] = useState<ChatRange>({ from: today, to: today });
  const [brand, setBrand] = useState("");
  const range = kind === "custom" ? custom : rangeOf(kind, today);
  const q = useSalesChatMetrics(range.from, range.to, brand);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Kỳ xem" className="flex flex-wrap gap-1">
          {CHAT_PERIODS.map((p) => (
            <button
              key={p.kind}
              type="button"
              aria-pressed={kind === p.kind}
              onClick={() => setKind(p.kind)}
              className={
                kind === p.kind
                  ? "min-h-11 rounded-md bg-primary px-3 text-sm text-white"
                  : "min-h-11 rounded-md border border-border px-3 text-sm text-muted hover:text-foreground"
              }
            >
              {p.label}
            </button>
          ))}
        </div>
        {kind === "custom" ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1">
              <span className="text-muted">Từ</span>
              <input
                type="date"
                aria-label="Từ ngày"
                value={custom.from}
                onChange={(e) => e.target.value && setCustom((c) => ({ ...c, from: e.target.value }))}
                className="min-h-11 min-w-0 rounded-md border border-border bg-white px-2"
              />
            </label>
            <label className="flex items-center gap-1">
              <span className="text-muted">Đến</span>
              <input
                type="date"
                aria-label="Đến ngày"
                value={custom.to}
                onChange={(e) => e.target.value && setCustom((c) => ({ ...c, to: e.target.value }))}
                className="min-h-11 min-w-0 rounded-md border border-border bg-white px-2"
              />
            </label>
          </div>
        ) : null}
        <select
          aria-label="Thương hiệu"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="min-h-11 min-w-0 rounded-md border border-border bg-white px-2 text-sm"
        >
          {CHAT_BRANDS.map((b) => (
            <option key={b.code} value={b.code}>
              {b.label}
            </option>
          ))}
        </select>
        {q.data?.updatedAt ? <span className="text-xs text-muted md:ml-auto">Cập nhật lúc {updatedAtText(q.data.updatedAt)}</span> : null}
      </div>

      {q.isError ? (
        <QueryErrorNote error={q.error} fallback="Không tải được số data và chốt" />
      ) : !q.data ? (
        <p className="text-sm text-muted">Đang tải…</p>
      ) : (
        <Body m={q.data} />
      )}
      <PancakeAccountsPanel />
    </div>
  );
}

function Money({ v }: { v: number | null | undefined }) {
  const mobile = useIsMobile();
  if (v == null) return <>—</>;
  return <span className="whitespace-nowrap text-[22px] md:text-[20px] xl:text-[22px]">{mobile ? formatVNDCompact(v) : formatVND(v)}</span>;
}

function Warnings({ m }: { m: SalesChatMetrics }) {
  const lines: string[] = [];
  if (m.stale && m.updatedAt) lines.push(`Số chậm từ ${updatedAtText(m.updatedAt)}.`);
  if (m.missingDays.length > 0) {
    lines.push(
      m.missingDays.length <= 3
        ? `Chưa có số ngày ${m.missingDays.map(dayText).join(", ")}.`
        : `Chưa có số ${m.missingDays.length} ngày trong kỳ, từ ${dayText(m.missingDays[0] ?? "")}.`,
    );
  }
  if (m.missingPages.length > 0) {
    lines.push(`Trang ${m.missingPages.map((p) => p.pageName || p.pageId).join(", ")} mất kết nối, tổng chưa gồm số mới của trang này.`);
  }
  if (m.missingSources.length > 0) {
    lines.push(`Chưa có ${[...new Set(m.missingSources.map(sourceLabel))].join(", ")}.`);
  }
  if (m.makeupCounted) {
    // Đơn Makeup đã nằm trong chốt và doanh số: không còn gì phải báo.
  } else if (m.ordersCounted) {
    lines.push(
      m.makeup
        ? `Chốt và doanh số chưa gồm đơn Makeup (GNPM). Data trang Makeup hiện riêng: ${maybeInt(m.makeup.dataNew)} mới, ${maybeInt(m.makeup.dataReturning)} cũ, không vào tỉ lệ chốt.`
        : "Chốt và doanh số chưa gồm đơn Makeup (GNPM).",
    );
  } else {
    lines.push("Đơn Makeup (GNPM) chưa về GNPF lúc tạo đơn nên chưa có chốt, doanh số, tỉ lệ. Đây là số data của trang Makeup.");
  }
  if (!m.byAssigneeSince) {
    lines.push("Chưa có số theo người được giao. Số theo trang vẫn đúng.");
  } else if (m.from < m.byAssigneeSince) {
    lines.push(`Số theo người có từ ngày ${dayText(m.byAssigneeSince)}.`);
  }
  if (lines.length === 0) return null;
  return (
    <div role="status" className="space-y-1 rounded-md border border-[var(--color-warning-text,#92400e)]/30 bg-[var(--color-warning-bg,#fffbeb)] p-3 text-sm">
      {lines.map((l) => (
        <p key={l}>{l}</p>
      ))}
    </div>
  );
}

function Tiles({ m }: { m: SalesChatMetrics }) {
  const t = m.totals;
  const w = `${String(m.window.fromHour).padStart(2, "0")}:00–${String(m.window.toHour).padStart(2, "0")}:00`;
  const phonePct = t.dataNew ? Math.round((t.phone * 100) / t.dataNew) : null;
  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <StatCard label="Data mới" value={maybeInt(t.dataNew)} sub="Khách nhắn lần đầu trong kỳ" />
      <StatCard label="Data cũ" value={maybeInt(t.dataReturning)} sub="Khách cũ có hoạt động trong kỳ" />
      <StatCard label="Chốt" value={maybeInt(t.closed)} sub={`Đơn thật · thẻ Pancake: ${maybeInt(t.closedByTag)}`} />
      <StatCard label="Tỉ lệ chốt" value={pctText(t.closeRatePct)} sub="Chốt trong kỳ ÷ data mới trong kỳ" />
      <StatCard label="Doanh số chốt" value={<Money v={t.revenue} />} sub="Đơn tạo trong kỳ, chưa VAT" />
      <StatCard label="Doanh số trên một data" value={<Money v={t.revenuePerData} />} sub="Doanh số chốt ÷ data mới" />
      <StatCard label="Trả lời trong 15 phút" value={pctText(t.replyPct)} sub={`Khách nhắn ${w}: ${maybeInt(t.replyWithin)}/${maybeInt(t.replyTotal)}`} />
      <StatCard
        label="Khách để lại điện thoại"
        value={maybeInt(t.phone)}
        sub={phonePct == null ? "Theo tài khoản Pancake" : `Bằng ${phonePct}% data mới`}
      />
    </div>
  );
}

function Body({ m }: { m: SalesChatMetrics }) {
  return (
    <>
      <Warnings m={m} />
      <Tiles m={m} />
      <SaleTable m={m} />
      {!m.limited && !m.viewAll && m.squads.length > 1 ? <SquadTable m={m} /> : null}
      {m.viewAll ? (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <PageTable m={m} />
            <BranchTable m={m} />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <FunnelCard m={m} />
            <CohortTable m={m} />
          </div>
        </>
      ) : null}
    </>
  );
}
